// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Header,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import { RetryAlert } from '../../Common/ErrorMessages';
import { convertDateTimePretty } from '../../../utils/ReadableConverter';
import { ApiError } from '../../../utils/serverErrors';
import { formatLabel } from '../PricingModels/pricingModels';
import { TER_GROUPS_PATH, TerGroupSummary } from '../TerGroups/terGroups';
import * as scopingModels from './scopingModels';
import { Loaded, useLoaded } from './useLoaded';

interface ScopingModelsListProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
  };
  navigate: (path: string) => void;
  canWrite: boolean;
}

// The summaries carry neither the 0–49% and 50–74% entries, the TER group nor the latest change
// set's status, so each row shows them from the model's detail, loaded after the list.
type Details = Record<string, scopingModels.ScopingModel | 'failed'>;

/** The draft's TER group by name, or by id until the TER groups load; '-' when no band uses TER. */
const terGroupText = (draft: scopingModels.Draft, terGroups: Loaded<TerGroupSummary[]>): string => {
  if (!scopingModels.isMultiplierDraft(draft) || !draft.terGroupId) {
    return '-';
  }
  const summary = (terGroups.value || []).find(group => group.terGroup === draft.terGroupId);
  return summary ? summary.groupName : draft.terGroupId;
};

const latestOf = (
  model: scopingModels.ScopingModelSummary,
  details: Details,
  show: (changeSet: scopingModels.ChangeSet) => string
): string => {
  const detail = details[model.weightSchemeId];
  if (detail === undefined) {
    return '…';
  }
  return detail === 'failed' || !detail.latestChangeSet ? '-' : show(detail.latestChangeSet);
};

export const ScopingModelsList = ({
  apiClient,
  navigate,
  canWrite,
}: ScopingModelsListProps): JSX.Element => {
  const [models, setModels] = useState<scopingModels.ScopingModelSummary[]>([]);
  const [details, setDetails] = useState<Details>({});
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);
  const [attempt, setAttempt] = useState(0);
  const terGroups = useLoaded<TerGroupSummary[]>(apiClient, TER_GROUPS_PATH);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadErrors([]);
    setDetails({});
    apiClient
      .httpGet(scopingModels.SCOPING_MODELS_PATH)
      .then(response => {
        if (!active) {
          return;
        }
        const loaded = response as scopingModels.ScopingModelSummary[];
        setModels(loaded);
        loaded.forEach(({ weightSchemeId }) => {
          apiClient.httpGet(scopingModels.scopingModelPath(weightSchemeId)).then(
            detail => {
              if (active) {
                setDetails(current => ({
                  ...current,
                  [weightSchemeId]: detail as scopingModels.ScopingModel,
                }));
              }
            },
            () => {
              if (active) {
                setDetails(current => ({ ...current, [weightSchemeId]: 'failed' }));
              }
            }
          );
        });
      })
      .catch((error: ApiError) => {
        if (active) {
          setModels([]);
          setLoadErrors(scopingModels.describeError(error));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return (): void => {
      active = false;
    };
  }, [apiClient, attempt]);

  const failedCount = Object.values(details).filter(detail => detail === 'failed').length;
  const createPath = `${scopingModels.SCOPING_MODELS_ROUTE}/new`;

  const columnDefinitions = [
    {
      id: 'name',
      header: 'Name',
      cell: (model: scopingModels.ScopingModelSummary): JSX.Element => (
        <Link to={scopingModels.scopingModelRoute(model.weightSchemeId)}>{model.name}</Link>
      ),
    },
    ...scopingModels.ZERO_TO_74_BANDS.map(band => ({
      id: band.weight,
      header: band.label,
      cell: (model: scopingModels.ScopingModelSummary): string =>
        latestOf(model, details, changeSet => scopingModels.entryText(changeSet.draft, band)),
    })),
    {
      id: 'terGroup',
      header: 'TER group',
      cell: (model: scopingModels.ScopingModelSummary): string =>
        latestOf(model, details, changeSet => terGroupText(changeSet.draft, terGroups)),
    },
    {
      id: 'status',
      header: 'Latest change set',
      cell: (model: scopingModels.ScopingModelSummary): string =>
        latestOf(model, details, changeSet => formatLabel(changeSet.status)),
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (model: scopingModels.ScopingModelSummary): string =>
        convertDateTimePretty(model.createdAt),
    },
    {
      id: 'author',
      header: 'Author',
      cell: (model: scopingModels.ScopingModelSummary): string => model.author,
    },
  ];

  return (
    <SpaceBetween size="m">
      {loadErrors.length > 0 && (
        <RetryAlert
          id="scopingModelsLoadError"
          header="Could not load scoping models"
          messages={loadErrors}
          retryId="retryScopingModels"
          onRetry={(): void => setAttempt(current => current + 1)}
        />
      )}
      {failedCount > 0 && (
        <Alert id="scopingModelsDetailsError" type="warning">
          {`Could not load the 0–49% and 50–74% entries, TER group and status of ${failedCount} scoping ${
            failedCount === 1 ? 'model' : 'models'
          }; open a model to see them.`}
        </Alert>
      )}
      <Table
        id="scopingModelsTable"
        items={models}
        loading={loading}
        loadingText="Loading scoping models"
        trackBy="weightSchemeId"
        columnDefinitions={columnDefinitions}
        header={
          <Header
            variant="h2"
            actions={
              canWrite && (
                <Button
                  id="createScopingModel"
                  variant="primary"
                  href={createPath}
                  onFollow={(event): void => {
                    event.preventDefault();
                    navigate(createPath);
                  }}
                >
                  Create scoping model
                </Button>
              )
            }
          >
            Scoping models
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            {loadErrors.length > 0 ? '' : 'No scoping models.'}
          </Box>
        }
      />
    </SpaceBetween>
  );
};

ScopingModelsList.propTypes = {
  apiClient: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired,
};

const ScopingModelsListView = (props: Omit<ScopingModelsListProps, 'apiClient'>): JSX.Element => (
  <ScopingModelsList apiClient={ApiClient} {...props} />
);

export default ScopingModelsListView;
